import React, { useState } from 'react';

import { QuestionFlowLoadingState } from 'components/QuestionFlow/QuestionFlowLoadingState';
import { useFlowThemeTransitionReady } from 'components/QuestionFlow/themes/FlowThemeTransition';
import { Button } from 'components/ui/button';
import { DebriefScreen, type RoleplayPracticeFocus } from './DebriefScreen';
import { PracticeScreen } from './PracticeScreen';
import { PrebriefScreen } from './PrebriefScreen';
import { RoleplayDebugPanel } from './RoleplayDebugPanel';
import { SafetyStopScreen } from './SafetyStopScreen';
import type { RoleplaySessionController } from './useRoleplaySession';
import type { RoleplayVoiceLatency } from './useRoleplayVoiceSession';
import './RoleplayPractice.css';

export type RoleplayScreensVariant = 'page' | 'embedded';

/**
 * The prebrief → practice → debrief screens shared by the standalone roleplay
 * page and the roleplay step embedded inside an ordinary question flow.
 *
 * The `page` variant owns the whole viewport. The `embedded` variant sizes
 * itself against the space left under the question-flow header and lets the
 * flow shell decide what "continue" and "back" mean.
 */
export const RoleplayScreens: React.FC<{
  controller: RoleplaySessionController;
  variant?: RoleplayScreensVariant;
  onContinue: () => void;
  onExit: () => void;
  onSafetyClose: () => void;
  onBack?: () => void;
  canContinue?: boolean;
  continueLabel?: string;
  showDebugPanel?: boolean;
  onStartGuidedVoice?: () => void;
}> = ({
  controller,
  variant = 'page',
  onContinue,
  onExit,
  onSafetyClose,
  onBack,
  canContinue = false,
  continueLabel,
  showDebugPanel = false,
  onStartGuidedVoice,
}) => {
  const isEmbedded = variant === 'embedded';
  // Voice playback timings are published by the practice surface before it
  // unmounts so the staff debug drawer can still show them during reflection.
  const [voiceLatency, setVoiceLatency] = useState<RoleplayVoiceLatency | null>(null);
  // "Practice this skill" on the scorecard carries its tip into the next prebrief.
  const [practiceFocus, setPracticeFocus] = useState<RoleplayPracticeFocus | null>(null);
  const [returningToPrebrief, setReturningToPrebrief] = useState(false);
  const practiceAgain = (focus: RoleplayPracticeFocus | null) => {
    setPracticeFocus(focus);
    setReturningToPrebrief(true);
    void controller.prepareRetry();
  };

  useFlowThemeTransitionReady(!controller.loading, { unthemed: true });

  if (controller.loading) return <QuestionFlowLoadingState />;
  if (!controller.scenario) {
    return (
      <main
        className={`flex ${isEmbedded ? 'rp-embedded-shell' : 'min-h-screen'} items-center justify-center p-6`}
        data-testid="roleplay-error"
      >
        <div className="max-w-md text-center">
          <p className="mb-4 text-foundation-base-darkbase-dark-500">
            {controller.error ?? 'This roleplay scenario is unavailable.'}
          </p>
          <Button onClick={() => void controller.reload()}>Try again</Button>
        </div>
      </main>
    );
  }

  return (
    <div
      className={`${isEmbedded ? 'rp-embedded' : 'min-h-screen'} text-foundation-base-darkbase-dark-500`}
      data-testid={isEmbedded ? 'roleplay-flow-step' : 'roleplay-page'}
    >
      {controller.screen === 'prebrief' && (
        <PrebriefScreen
          scenario={controller.scenario}
          processing={controller.processing}
          error={controller.error}
          onStart={(mode) => void controller.startPractice(mode)}
          onBack={onBack}
          onStartGuidedVoice={onStartGuidedVoice}
          focus={practiceFocus}
          returning={returningToPrebrief}
        />
      )}
      {controller.screen === 'practice' && (
        <PracticeScreen controller={controller} onVoiceLatencyChange={setVoiceLatency} />
      )}
      {controller.screen === 'support' && (
        <SafetyStopScreen
          onClose={onSafetyClose}
          processing={controller.processing}
          error={controller.error}
          onStartOver={async () => {
            // Closing the paused attempt keeps its safety record; the learner then starts fresh.
            if (await controller.endPractice('crisis_acknowledged')) await controller.prepareRetry();
          }}
        />
      )}
      {controller.screen === 'debrief' && (
        <DebriefScreen
          controller={controller}
          onContinue={onContinue}
          onExit={onExit}
          onBack={onBack}
          canContinue={canContinue}
          continueLabel={continueLabel}
          onPracticeAgain={practiceAgain}
        />
      )}
      {showDebugPanel ? (
        <RoleplayDebugPanel controller={controller} voiceLatency={voiceLatency} />
      ) : null}
    </div>
  );
};
